import { baseLocale, type Locale } from '$lib/paraglide/runtime';
import type { WikiPage } from './frontmatter';

/**
 * Czysta logika SEO stron wiki (SAM-552) — testowalna w projekcie `logic`.
 * Origin hardcoded jak w JSON-LD stron marketingowych (features/pricing).
 */
export const WIKI_SITE_ORIGIN = 'https://samlla.app';

export interface WikiArticleSeo {
	/** Ścieżka canonical; dla strony fallbackowej — oryginał EN. */
	canonicalPath: string;
	/**
	 * Klaster hreflang (tylko realne tłumaczenia). Strona fallbackowa nie
	 * emituje hreflang — jej canonical wskazuje poza nią, więc nie jest
	 * stroną kanoniczną i nie wchodzi do klastra.
	 */
	hreflang: Partial<Record<Locale, string>> | undefined;
}

export function buildWikiArticleSeo(args: {
	locale: Locale;
	fallback: boolean;
	realHrefs: Partial<Record<Locale, string>>;
}): WikiArticleSeo {
	const { locale, fallback, realHrefs } = args;
	const enHref = realHrefs[baseLocale];
	if (enHref === undefined) {
		// buildWikiIndex gwarantuje wersję EN — to tylko strażnik typów.
		throw new Error('Wiki page has no EN href');
	}
	if (fallback) {
		return { canonicalPath: enHref, hreflang: undefined };
	}
	return { canonicalPath: realHrefs[locale] ?? enHref, hreflang: realHrefs };
}

/** Article JSON-LD dla strony wiki. `lang` = język TREŚCI (fallback → en). */
export function buildWikiArticleJsonLd(args: {
	page: Pick<WikiPage, 'title' | 'description' | 'updated' | 'lang'>;
	canonicalPath: string;
}): Record<string, unknown> {
	return {
		'@context': 'https://schema.org',
		'@type': 'TechArticle',
		headline: args.page.title,
		description: args.page.description,
		dateModified: args.page.updated,
		inLanguage: args.page.lang,
		mainEntityOfPage: `${WIKI_SITE_ORIGIN}${args.canonicalPath}`,
		publisher: {
			'@type': 'Organization',
			name: 'Samlla',
			url: WIKI_SITE_ORIGIN
		}
	};
}

/** BreadcrumbList: Samlla Wiki (home w bieżącym języku) → artykuł. */
export function buildWikiBreadcrumbJsonLd(args: {
	homeLabel: string;
	homePath: string;
	title: string;
	articlePath: string;
}): Record<string, unknown> {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: args.homeLabel,
				item: `${WIKI_SITE_ORIGIN}${args.homePath}`
			},
			{
				'@type': 'ListItem',
				position: 2,
				name: args.title,
				item: `${WIKI_SITE_ORIGIN}${args.articlePath}`
			}
		]
	};
}
